import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { ArrowDown, Zap } from "lucide-react";
import { Navbar } from "./components/Navbar";
import { Hero3D } from "./components/Hero3D";
import { Marquee } from "./components/Marquee";
import { Stats } from "./components/Stats";
import { Services } from "./components/Services";
import { Process } from "./components/Process";
import { CaseStudies } from "./components/CaseStudies";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { WhatsAppButton } from "./components/WhatsAppButton";

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden tech-grid">
    <Hero3D />
    <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_35%,#0A0C0E_88%)]" />
    <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-b from-[#0A0C0E]/70 via-[#0A0C0E]/40 to-[#0A0C0E]/85 lg:from-[#0A0C0E]/30 lg:via-transparent lg:to-[#0A0C0E]/60" />

    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 w-full pointer-events-none">
      <div className="max-w-3xl">
        <div className="rise-in inline-flex items-center gap-2.5 rounded-full border border-[#A3E635]/30 bg-black/50 backdrop-blur px-4 py-2 mb-8 pointer-events-auto" style={{ animationDelay: "0.1s" }}>
          <span className="h-2 w-2 rounded-full bg-[#A3E635] animate-pulse-dot" />
          <span className="font-mono2 text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#A3E635]">
            Brand Engineering • Performance Marketing • Bengaluru
          </span>
        </div>

        <h1 data-testid="hero-headline" className="rise-in font-heading text-[6.3vw] lg:text-6xl xl:text-7xl font-extrabold uppercase tracking-tight leading-[1.0] lg:leading-[0.95] mb-7" style={{ animationDelay: "0.25s" }}>
          We manufacture<br />
          <span className="text-[#A3E635] text-glow-lime">content IP</span> &<br />
          performance engines
        </h1>

        <p data-testid="hero-subtitle" className="rise-in text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-10" style={{ animationDelay: "0.4s" }}>
          We build brands, grow audiences, generate leads — and turn digital attention into measurable business growth. Your entire content & growth team, under one roof.
        </p>

        <div className="rise-in flex flex-wrap items-center gap-4 pointer-events-auto" style={{ animationDelay: "0.55s" }}>
          <a
            href="#contact"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#A3E635] px-8 py-4 font-mono2 text-sm font-bold tracking-[0.15em] uppercase text-black transition-all duration-300 hover:bg-[#84CC16] hover:shadow-[0_0_44px_rgba(163,230,53,0.5)]"
          >
            <Zap size={16} className="transition-transform duration-300 group-hover:rotate-12" />
            Book Free Growth Audit
          </a>
          <a
            href="#work"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-black/40 backdrop-blur px-8 py-4 font-mono2 text-sm font-bold tracking-[0.15em] uppercase text-white transition-all duration-300 hover:border-[#A3E635]/60 hover:text-[#A3E635]"
          >
            See The Proof
          </a>
        </div>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-500">
      <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase">Scroll</span>
      <ArrowDown size={16} className="animate-bounce" />
    </div>
  </section>
);

const Home = () => (
  <div className="grain bg-[#0A0C0E] min-h-screen">
    <Navbar />
    <Hero />
    <Marquee />
    <Stats />
    <Services />
    <Process />
    <CaseStudies />
    <Contact />
    <Footer />
    <WhatsAppButton />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}

export default App;
